import posthog, { type BootstrapConfig } from "posthog-js"

import { safePostHogProperties } from "@/lib/analytics"
import { AppEnvironment, getAppEnvironment } from "@/lib/app-environment"

const currentAppEnvironment = getAppEnvironment(process.env.NODE_ENV)
const isProduction = currentAppEnvironment === AppEnvironment.Production
const isPosthogEnabled =
  process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true" || isProduction

let currentPageTemplate: string | null = null

export function setPostHogPageTemplate(template: string | null) {
  currentPageTemplate = template
}

// Reads distinct_id / session_id passed in the URL hash by the marketing site
// (coval.ai) so a visitor's session, replay, and flag context carry into the
// app on a cross-root-domain hop. Scrubs the hash after reading so the IDs
// don't leak via Referer or browser history.
function readCrossDomainBootstrap(): BootstrapConfig | undefined {
  if (typeof window === "undefined" || !window.location.hash) {
    return undefined
  }

  const params = new URLSearchParams(window.location.hash.slice(1))
  const distinctID = params.get("distinct_id") ?? undefined
  const sessionID = params.get("session_id") ?? undefined
  if (!distinctID && !sessionID) {
    return undefined
  }

  params.delete("distinct_id")
  params.delete("session_id")
  const remaining = params.toString()
  const newHash = remaining ? `#${remaining}` : ""
  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search + newHash,
  )

  return { distinctID, sessionID }
}

if (typeof window !== "undefined" && isPosthogEnabled) {
  const bootstrap = readCrossDomainBootstrap()

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    // Use proxy in production to bypass ad blockers
    api_host: isProduction ? "/ingest" : "https://us.i.posthog.com",
    ui_host: "https://us.posthog.com",
    person_profiles: "identified_only",
    // A share link is a capability. Do not record the document or its URL.
    disable_session_recording:
      window.location.pathname.startsWith("/shared/notebooks/"),
    ...(bootstrap ? { bootstrap } : {}),
    sanitize_properties: (properties) => safePostHogProperties(properties),
    before_send: (event) => {
      if (window.location.pathname.startsWith("/shared/notebooks/")) {
        return null
      }
      if (event && currentPageTemplate) {
        event.properties = {
          ...event.properties,
          page_template: currentPageTemplate,
        }
      }
      return event
    },
    loaded: (posthog) => {
      if (!isProduction) {
        posthog.debug()
      }
    },
  })
}

export default posthog
